// Settings › Devices (tech's M3 audit): every iPad paired here, for the owner and managers. Each says
// who it's paired to, its app (and whether it should be updated), when it last synced, what it hasn't
// sent and since when (the 12-hour alert's state, the server's own rule), and what it sent after it
// was signed out, waiting in Review. "Sign out…" signs one iPad out with a reason (devices.revoke):
// it wipes itself the next time it connects, so there is no separate wipe. An iPad the person
// signed out of themselves keeps what it hadn't sent, sealed for them (QA-177): the owner sees how
// many, whose and since when, and can discard them for good (devices.discardSealed, BL-073).

import { useCallback, useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { Loading } from '../shared/Loading.tsx';
import {
  platformWord,
  quarantinedWords,
  sealedWords,
  statusWords,
  syncedWords,
  unsentWords,
  type DeviceRow,
} from './devices-model.ts';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function DevicesTab({
  timeZone,
  onReview,
  owner = false,
}: {
  timeZone: string;
  onReview?: () => void;
  /** Only the owner discards what a person left sealed on an iPad. */
  owner?: boolean;
}) {
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<DeviceRow | null>(null);
  const [discarding, setDiscarding] = useState<DeviceRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setDevices((await client.call<{ devices: DeviceRow[] }>('devices.list', {})).devices);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  if (!devices) return <Loading error={error} what="the devices" retry={() => void load()} />;
  const now = new Date();
  const live = devices.filter((d) => !d.revokedAt);
  const out = devices.filter((d) => d.revokedAt);
  const row = (d: DeviceRow) => {
    const status = statusWords(d, timeZone);
    const held = quarantinedWords(d.quarantined);
    const kept = sealedWords(d, timeZone);
    return (
      <li key={d.id} className="person device">
        <div className="who">
          <b>{d.personName ?? 'Someone no longer here'}</b>
          <span className="muted small">
            {d.name} · {platformWord(d.platform)} · app {d.appVersion}
          </span>
          {(d.outOfDate || d.stale) && (
            <span className="chips">
              {d.outOfDate && <span className="chip warn">Out of date: update the app</span>}
              {d.stale && <span className="chip warn">Not synced for 12 hours</span>}
            </span>
          )}
        </div>
        <div className="facts">
          <span>{syncedWords(d, now, timeZone)}</span>
          {/* kept for its person rather than waiting to send: said once, below */}
          {!kept && (
            <span className={d.stale ? 'warn-text' : d.pendingCount ? '' : 'muted'}>
              {unsentWords(d, now, timeZone)}
            </span>
          )}
          {held && (
            <span>
              {held}
              {onReview && (
                <>
                  {' '}
                  <button type="button" className="btn quiet small" onClick={onReview}>
                    Open Review
                  </button>
                </>
              )}
            </span>
          )}
          <span className={d.revokedAt ? 'muted' : ''}>{status.head}</span>
          {status.wipe && <span className="muted small">{status.wipe}</span>}
          {kept && (
            <span className="kept" data-testid={`kept-${d.id}`}>
              <span className="warn-text">{kept.head}</span> <span className="muted small">{kept.open}</span>
              {owner && d.staffId && (
                <>
                  {' '}
                  <button type="button" className="btn quiet small" onClick={() => setDiscarding(d)}>
                    Discard them…
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        {!d.revokedAt && (
          <div className="give">
            <button type="button" className="btn soft" onClick={() => setRevoking(d)}>
              Sign out…
            </button>
          </div>
        )}
      </li>
    );
  };
  return (
    <section className="devices" aria-label="Devices">
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      {devices.length === 0 ? (
        <p className="muted">No iPads are paired yet. A technician pairs one by signing in on it.</p>
      ) : (
        <>
          {live.length ? (
            <ul className="rows people sheet" aria-label="Signed-in devices">
              {live.map(row)}
            </ul>
          ) : (
            <p className="muted">No iPad is signed in right now.</p>
          )}
          {out.length > 0 && (
            <details className="signed-out">
              <summary>Signed out ({out.length})</summary>
              <ul className="rows people sheet" aria-label="Signed-out devices">
                {out.map(row)}
              </ul>
            </details>
          )}
        </>
      )}
      {discarding && (
        <DiscardKeptDialog
          device={discarding}
          onClose={() => setDiscarding(null)}
          onDone={async (t) => {
            setDiscarding(null);
            setToast(t);
            await load();
          }}
        />
      )}
      {revoking && (
        <RevokeDialog
          device={revoking}
          onClose={() => setRevoking(null)}
          onDone={async (t) => {
            setRevoking(null);
            setToast(t);
            await load();
          }}
        />
      )}
    </section>
  );
}

/** Sign one iPad out, with a reason: the server's words for what happens, and its refusal if any. */
function RevokeDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceRow;
  onClose: () => void;
  onDone: (toast: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const [why, setWhy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const whose = device.personName ? `${device.personName}’s ${device.name}` : device.name;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('devices.revoke', { deviceId: device.id, reason: why.trim() });
      await onDone(`${whose} is signed out. It wipes itself the next time it connects.`);
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="revoke-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h3 id="revoke-title" className="sheet-title">
          Sign out {whose}
        </h3>
        <p className="muted">
          Signs out this iPad now. Anything it hasn’t sent comes to you to review, and it wipes itself the
          next time it connects. {device.personName ? `${device.personName}’s` : 'Their'} sign-in elsewhere
          stays as it is.
        </p>
        {device.pendingCount > 0 && (
          <p className="warn-text">
            It still has {device.pendingCount === 1 ? '1 change' : `${device.pendingCount} changes`} it hasn’t
            sent. They come to Review once it connects.
          </p>
        )}
        <label>
          Why (lost, swapped, someone left)
          <input className="field" value={why} maxLength={300} onChange={(e) => setWhy(e.target.value)} />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button type="submit" className="btn danger" disabled={busy || !why.trim()}>
            {busy ? 'Signing out…' : 'Sign out this iPad'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * The owner discards what a person left sealed on their iPads (QA-177): never sent, by anyone, and
 * it can't be undone. With a reason, as the server asks.
 */
function DiscardKeptDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceRow;
  onClose: () => void;
  onDone: (toast: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const [why, setWhy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const who = device.personName ?? 'this person';
  const n = device.sealed?.count ?? 0;
  const changes = n === 1 ? 'the 1 change' : `the ${n} changes`;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('devices.discardSealed', { staffId: device.staffId, reason: why.trim() });
      await onDone(
        `${who}’s kept changes are discarded. The iPad drops them the next time anyone signs in on it.`,
      );
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="discard-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h3 id="discard-title" className="sheet-title">
          Discard {who}’s kept changes
        </h3>
        <p className="warn-text">
          {changes.charAt(0).toUpperCase() + changes.slice(1)} kept on {device.name} will never be sent, to
          you or to anyone, and this can’t be undone. Anything {who} left kept on another iPad here is
          discarded too.
        </p>
        <p className="muted">
          For when {who} won’t sign in on it again: they left, or the iPad goes to someone else.
        </p>
        <label>
          Why
          <input className="field" value={why} maxLength={300} onChange={(e) => setWhy(e.target.value)} />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button type="submit" className="btn danger" disabled={busy || !why.trim()}>
            {busy ? 'Discarding…' : 'Discard them'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
